import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem, Card3D } from "../../../components/animations/AnimatedElements";

const products = [
  {
    icon: "ri-vidicon-ai-line",
    tag: "CORE",
    title: "AI & Digital Twin\n통합 ADX 독창적 기술",
    desc: "ADX 도입 및 고객 민원 솔루션 설계 구축",
    features: ["영상 데이터화·분석", "API 연동 지원", "디지털트윈 연계", "다도메인 적용"],
    img: "https://storage.readdy-site.link/project_files/4f50973d-6cdf-41d1-9456-124b0c520de3/985b3920-e8da-4135-8fc4-6c5e590166a9_KakaoTalk_20260319_214358578_03.png?v=07b9f1c68a154786c6bed15be1f43f87",
  },
  {
    icon: "ri-train-line",
    tag: "철도",
    title: "지하철 인파사고\n예방 시스템",
    desc: "AI 기반 실시간 역사 혼잡도 예측 및 관리 모델. 서울교통공사 등 철도 분야 사회안전망 최적화.",
    features: ["실시간 혼잡도 예측", "사고 사전 감지", "철도 분야 특화", "관제 시스템 연동"],
    img: "/demo-previews/reporting-web.png",
  },
  {
    icon: "ri-flight-takeoff-line",
    tag: "공항",
    title: "지능형 AI 및\n여객흐름 통합 솔루션",
    desc: "인천공항 여객 이용 시간 분석 및 출국 소요 시간을 AI로 측정. 디지털트윈 기반 혼잡도 시각화와 여객흐름 최적화.",
    features: ["여객흐름 AI 분석", "혼잡도 실시간 예측", "디지털트윈 시각화", "출국 소요시간 측정"],
    img: "/demo-previews/airport-congestion-monitoring.png",
  },
  {
    icon: "ri-fingerprint-line",
    tag: "AI 업무지원",
    title: "자연어 및 이미지 해석을 통한\nAI 업무환경 지원",
    desc: "자연어 및 이미지 해석을 통한 AI 업무환경 지원. 스마트한 업무 자동화와 지능형 업무 보조 시스템.",
    features: ["자연어 해석", "이미지 분석", "업무 자동화", "AI 업무 보조"],
    img: "/products/security-dashboard.jpg",
    video: "/products/object-tracking-preview-01.mp4",
  },
  {
    icon: "ri-flashlight-line",
    tag: "에너지",
    title: "전력데이터 관리\nPDU 솔루션",
    desc: "AI 서버 전력 측정 및 관리. 전력 불안전 상황을 사전에 알림하여 서버 장애를 예방.",
    features: ["실시간 전력 측정", "이상 징후 사전 알림", "AI 서버 최적화", "장애 예방 시스템"],
    img: "/demo-previews/system-resource-monitoring.png",
  },
];

export default function ProductsSection() {
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <section id="products" className="py-28 bg-brand-darkAlt relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">Products & Services</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
            <h2 className="section-title">
              제품 · <span className="text-cyan-400">서비스</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-md">
              ADX 플랫폼 기반 컨설팅을 통해 도출된 고객 맞춤형 AI·디지털트윈 솔루션 포트폴리오입니다.
            </p>
          </div>

          {/* Featured Product - Bento Style */}
          <div className="grid lg:grid-cols-5 gap-6 mb-8">
            {/* Main Image */}
            <div className="lg:col-span-3 relative h-72 lg:h-96 rounded-xl overflow-hidden group">
              <AnimatePresence mode="wait">
                {products[activeIdx].video ? (
                  <motion.video
                    key={`${activeIdx}-video`}
                    src={products[activeIdx].video}
                    poster={products[activeIdx].img}
                    className="w-full h-full object-cover object-top"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.4 }}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <motion.img
                    key={`${activeIdx}-image`}
                    src={products[activeIdx].img}
                    alt={products[activeIdx].title.replace("\n", " ")}
                    className="w-full h-full object-cover object-top"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                  />
                )}
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-darkAlt/80 via-transparent to-transparent" />
              
              {/* Tag */}
              <motion.div
                className="absolute top-4 left-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="text-xs font-bold tracking-widest bg-cyan-500 text-brand-dark px-3 py-1.5 rounded-md">
                  {products[activeIdx].tag}
                </span>
              </motion.div>

              {/* Overlay Info */}
              <motion.div
                className="absolute bottom-4 left-4 right-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-2xl font-bold text-white whitespace-pre-line drop-shadow-lg">
                  {products[activeIdx].title}
                </h3>
              </motion.div>
            </div>

            {/* Product Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIdx}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="lg:col-span-2 bg-brand-dark border border-cyan-900/30 rounded-xl p-7 flex flex-col justify-between"
              >
                <div>
                  <motion.div
                    className="icon-box-lg mb-4"
                    whileHover={{ rotate: 5 }}
                  >
                    <i className={`${products[activeIdx].icon} text-cyan-400 text-2xl`} />
                  </motion.div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">
                    {products[activeIdx].desc}
                  </p>
                </div>
                <StaggerContainer className="grid grid-cols-2 gap-2" staggerDelay={0.05}>
                  {products[activeIdx].features.map((f) => (
                    <StaggerItem key={f}>
                      <div className="flex items-center gap-2 text-xs text-gray-300 bg-cyan-500/5 border border-cyan-900/30 rounded px-3 py-2">
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full flex-shrink-0" />
                        {f}
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Product Grid Selector - Enhanced */}
          <StaggerContainer
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
            staggerDelay={0.05}
          >
            {products.map((product, idx) => (
              <StaggerItem key={product.title}>
                <Card3D>
                  <motion.button
                    onClick={() => setActiveIdx(idx)}
                    role="tab"
                    aria-selected={activeIdx === idx}
                    aria-label={`${product.tag} - ${product.title.replace("\n", " ")}`}
                    className={`text-left p-4 rounded-lg border transition-all duration-300 w-full ${
                      activeIdx === idx
                        ? "border-cyan-500/60 bg-cyan-500/10 shadow-glow-sm"
                        : "border-cyan-900/20 bg-brand-dark hover:border-cyan-900/50"
                    }`}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className={`text-xs font-bold tracking-wider block mb-2 ${
                      activeIdx === idx ? "text-cyan-400" : "text-gray-500"
                    }`}>
                      {product.tag}
                    </span>
                    <motion.div
                      className="w-7 h-7 flex items-center justify-center mb-1"
                      animate={activeIdx === idx ? { scale: 1.1 } : { scale: 1 }}
                    >
                      <i className={`${product.icon} ${
                        activeIdx === idx ? "text-cyan-400" : "text-gray-500"
                      } text-base`} />
                    </motion.div>
                    <p className={`text-xs leading-tight whitespace-pre-line ${
                      activeIdx === idx ? "text-white" : "text-gray-500"
                    }`}>
                      {product.title}
                    </p>
                  </motion.button>
                </Card3D>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </AnimatedSection>
      </div>
    </section>
  );
}
